import { Button, Col, Input, Row, Space, Switch, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EyeOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Category, Permissions } from "../../../types/General";
import { useUpdateCategoryByIdMutation } from "../../../services/main";
import { Dispatch, SetStateAction } from "react";
import { showError, showToast } from "../../../helpers/toast";
import useAuth from "../../../hooks/useAuth";
import { isValidInput } from "../../../utils/validations";

const ApprovedCategories = ({
  page,
  total,
  categories,
  searchTerm,
  modulePermissions,
  setPage,
  setSearchTerm,
  setApprovedCategories,
}: {
  categories: Category[];
  setApprovedCategories: Dispatch<SetStateAction<Category[]>>;
  page: number;
  total: number;
  modulePermissions?: Permissions | null;
  searchTerm: string;
  setPage: Dispatch<SetStateAction<number>>;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}) => {
  const user = useAuth();
  const navigate = useNavigate();
  const [updateCategoryById] = useUpdateCategoryByIdMutation();

  const updateStatus = async (id: string, state: boolean) => {
    const response = await updateCategoryById({
      category_id: id,
      body: {
        is_active: state,
      },
    }).unwrap();
    if (response?.code === 200) {
      const updatedData = categories.map((x) => {
        if (x.id === parseInt(id, 10)) {
          return {
            ...x,
            is_active: state,
          };
        }
        return x;
      });
      setApprovedCategories(updatedData);
      showToast("Status updated successfully");
    } else {
      showError("Please try again");
    }
  };

  const renderSwitch = (_: any, data: Category) => {
    return (
      <Switch
        onChange={(val) => updateStatus(`${data?.id}`, val)}
        size="small"
        checked={data?.is_active}
      />
    );
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const columns: ColumnsType<Category> = [
    {
      title: "S.no.",
      key: "sno",
      render: (value, item, index) => (page - 1) * 10 + index + 1,
      align: "center",
    },
    {
      title: "Logo",
      dataIndex: "logo",
      key: "logo",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <img
            alt=""
            src={
              record?.category_image?.media_file_url ||
              "https://visualpharm.com/assets/919/Picture-595b40b85ba036ed117dc360.svg"
            }
            style={{ width: 50, height: "auto" }}
          />
        </Space>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (_, record) => (
        <div>
          {record?.description?.length > 50
            ? `${record?.description?.slice(0, 50)}...`
            : record?.description}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Button
            onClick={(e) => {
              e.preventDefault();
              navigate(`/manage-categories/details/${record?.id}`);
            }}
          >
            <EyeOutlined style={{ color: "#52c41a" }} />
          </Button>

          {modulePermissions?.is_edit || user?.role === 1 ? (
            <Button
              onClick={(e) => {
                e.preventDefault();
                window.open(`/manage-categories/${record?.id}`, "_blank");
              }}
            >
              <EditOutlined color="#08c" />
            </Button>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: renderSwitch,
      className: modulePermissions?.is_edit || user?.role === 1 ? "" : "hidden",
    },
  ];

  return (
    <div>
      <div className="srch">
        <Row gutter={16}>
          <Col xs={24} md={7}>
            <Input
              value={searchTerm}
              onChange={(val) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
              allowClear
              placeholder="Search"
              prefix={<SearchOutlined style={{ fontSize: "20px" }} />}
              // style={{ height: 55, borderRadius: 10, minWidth: "360px" }}
              className="search-bar"
            />
          </Col>
        </Row>
        <Table
          className="table-container"
          columns={columns}
          dataSource={categories || []}
          pagination={{
            defaultPageSize: 10,
            current: page,
            onChange: onPageChange,
            total,
          }}
        />
      </div>
    </div>
  );
};

export default ApprovedCategories;
